'use client'
//seed 200304498

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  CONTEXT  —  three-hero.tsx                                               ║
 * ║  Animated 3D fractal node-graph rendered as a fixed full-screen hero bg.  ║
 * ║  Stack: React Three Fiber · Three.js · postprocessing · Framer Motion     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * ── FILE SECTIONS (in order) ─────────────────────────────────────────────────
 *   MATH UTILS          random(), shuffleWithSeed(), cubeVertices(), CUBE_EDGES
 *   TREE GENERATION     jitterOffset, generateDefault, generateRandomBase,
 *                       generateCubeStructure  (GENERATOR_TYPE switch)
 *   CONSTANTS           all timing, color, opacity, post-processing knobs
 *   CHAIN & DEPTH       siblingToLetter, computeLineageMap, computeDepthMap,
 *                       buildChainData
 *   CUBE STRUCTURE      CubeStructure R3F component  (the main scene)
 *   POST-PROCESSING     PostProcessing R3F component
 *   SCENE ENVIRONMENT   TransparentBackground, SceneFog
 *   UI & LAYOUT         ParallaxWrapper, Slider, ThreeHero (root export)
 *
 * ── GENERATION  (once at module load — SCENE_DATA IIFE, fixed seed) ─────────
 *   generateRandomBase()
 *     Builds a fractal CubeTree: flat Cube[] + parentIndices[].
 *     Seed is fixed to FIXED_SEED = 275616998 — shape is stable across loads.
 *
 *   jitterOffset(vertexIdx, tier, magnitude)
 *     Randomised [x,y,z] displacement for child placement.
 *     JITTER_MODE = 'primes'   → coprime multipliers, maximum axis decorrelation.
 *     JITTER_MODE = 'harmonic' → multiples of 3, more coherent spread. (active)
 *
 *   buildChainData()
 *     Extracts all root→leaf paths, keeps longest up to MAX_CHAINS.
 *     Filter: chain leaves must have ≥ 2 siblings (no lone-child terminals).
 *     Builds cubeToChainPos reverse-lookup for O(1) blink checks.
 *
 *   allCubeGeoms          BoxGeometry + EdgesGeometry per cube, size-cached.
 *   precomputedNodeStates positive/negative + targetOpacity per node.
 *                         Leaf states: seeded random (genSeed + idx).
 *                         Parent states: propagated bottom-up from children.
 *                         All decided at generation — zero runtime branching.
 *   chainLineSegments     Per-segment BufferGeometry for chain connector lines;
 *                         opacity = lerp(MIN, MAX) by depth.
 *
 * ── RENDER  (static JSX — CubeStructure never re-renders after mount) ────────
 *   Every cube's fill mesh, dashed edge outline, and label group are rendered
 *   once at mount in hidden state. Materials are pre-registered into:
 *     fillMaterialsRef   Map<cubeIdx, MeshBasicMaterial>
 *     edgeMaterialsRef   Map<cubeIdx, LineDashedMaterial>
 *     labelGroupsRef     Map<cubeIdx, THREE.Group>
 *   All nodes start TEAL. Negative nodes flip to RED in useFrame the moment
 *   their verification transition clock is first set.
 *
 * ── ANIMATION  (useFrame — zero React setState calls in the hot path) ─────────
 *   A  Scene rotation
 *        groupRef.rotation.y += elapsed * ROTATION_SPEED_Y
 *
 *   B  Chain traveller  (chainStateRef state machine)
 *        Picks a random chain, fires a light pulse node-by-node.
 *        Each hop: blink material rises (BLINK_RISE_MS) then fades (BLINK_FADE_MS).
 *        On pulse arrival at leaf: marks node verified in cubeVerificationRef,
 *        looks up pre-computed state, bubbles up to parents if all siblings done.
 *        No React setState — geometry is pre-existing.
 *
 *   C  Fill / edge / label transition
 *        Iterates cubeVerificationRef each frame.
 *        Fill:  eases UNVERIFIED_BASE_FILL_OPACITY → targetOpacity (ease-out²).
 *        Edges: fades 0 → VERIFIED_EDGE_OPACITY in parallel.
 *        Label: set visible=true when elapsed ≥ FILL_TRANSITION_MS.
 *        Color flip (TEAL → RED for negative) happens once on first frame seen.
 *
 * ── POST-PROCESSING  (useFrame priority 1, runs after scene) ─────────────────
 *   RenderPass → BloomEffect → VignetteEffect → BrightnessContrastEffect
 *              → LensDistortionEffect → NoiseEffect (grain)
 *   Hardcoded dark-mode values. Rebuilds EffectPass when options prop changes.
 *   All params tunable live via the Effect sliders panel (portalled to <body>).
 *
 * ── CANVAS / LAYOUT ──────────────────────────────────────────────────────────
 *   Canvas is position:fixed, fills viewport, alpha:true (transparent bg).
 *   TransparentBackground sets scene.background = null.
 *   SceneFog applies FogExp2 (density 0.4) in dark-mode navy.
 *   PARALLAX_ENABLED = false  (canvas stays fixed on scroll).
 *   Set to true to restore scroll-driven translateY(scrollY * 0.5).
 *
 * ── SEED ─────────────────────────────────────────────────────────────────────
 *   FIXED_SEED = 275616998 — chosen for visual quality, baked in at module load.
 *   BEAUTIFUL_SEEDS = [686533348, 127924221] — known good structures.
 *   To lock a seed: replace the useState initialiser with a constant.
+ * ─────────────────────────────────────────────────────────────────────────────
 */

import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Text, Billboard } from '@react-three/drei'
import { Suspense, useRef, useMemo, useState, useEffect } from 'react'
import {
  STATIC_CUBES,
  STATIC_PARENT_INDICES,
  STATIC_CHAINS,
  STATIC_BLINK_CUBE_INDICES,
  STATIC_CUBE_TO_CHAIN_POS,
  STATIC_NODE_STATES,
  STATIC_DEPTH_MAP,
  STATIC_MAX_DEPTH,
  STATIC_LINEAGE,
  STATIC_CHILDREN,
  CHAIN_PLAY_ORDER,
} from './scene-data'
import * as THREE from 'three'
import { motion } from 'framer-motion'
import {
  BlendFunction,
  Effect,
  EffectComposer,
  EffectPass,
  RenderPass,
  BloomEffect,
  VignetteEffect,
  BrightnessContrastEffect,
  LensDistortionEffect,
  NoiseEffect,
} from 'postprocessing'

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  MATH UTILS                                                              ║
// ╚══════════════════════════════════════════════════════════════════════════╝

function random(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random(seed + i) * (i + 1)) % (i + 1)
    ;[result[i], result[j]] = [result[j]!, result[i]!]
  }
  return result
}

/** Number of chains that light up automatically on page load (time-driven). */
const INITIAL_CHAINS_COUNT = 10

/**
 * Pre-shuffled list of node (cube) indices driven by scroll.
 * Capped at 1000 — so reaching the bottom of the page verifies exactly
 * 1000 nodes (or all nodes if fewer exist). Nodes already lit by the
 * time-based initial chains are silently skipped.
 */
const SCROLL_VERIFY_NODE_ORDER: number[] = seededShuffle(
  STATIC_CUBES.map((_, i) => i),
  99371,
).slice(0, 1000)

function cubeVertices(cx: number, cy: number, cz: number, half: number) {
  const h = half
  return [
    [cx - h, cy - h, cz - h],
    [cx + h, cy - h, cz - h],
    [cx + h, cy + h, cz - h],
    [cx - h, cy + h, cz - h],
    [cx - h, cy - h, cz + h],
    [cx + h, cy - h, cz + h],
    [cx + h, cy + h, cz + h],
    [cx - h, cy + h, cz + h],
  ] as [number, number, number][]
}

const CUBE_EDGES = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 0],
  [4, 5],
  [5, 6],
  [6, 7],
  [7, 4],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
]

interface Cube {
  cx: number
  cy: number
  cz: number
  half: number
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  CONSTANTS & CONFIGURATION                                               ║
// ╚══════════════════════════════════════════════════════════════════════════╝

function cubeToLineSegments(cube: Cube): number[] {
  const verts = cubeVertices(cube.cx, cube.cy, cube.cz, cube.half)
  const positions: number[] = []
  for (const [a, b] of CUBE_EDGES) {
    const [x1, y1, z1] = verts[a]
    const [x2, y2, z2] = verts[b]
    positions.push(x1, y1, z1, x2, y2, z2)
  }
  return positions
}

function depthToColor(t: number): [number, number, number] {
  const near = [0.055, 0.647, 0.914] // #0ea5e9 — bright blue at depth
  const far = [0.04, 0.08, 0.14] // dark navy at distance
  return [
    near[0] * t + far[0] * (1 - t),
    near[1] * t + far[1] * (1 - t),
    near[2] * t + far[2] * (1 - t),
  ]
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  CONSTANTS & CONFIGURATION                                               ║
// ╚══════════════════════════════════════════════════════════════════════════╝

// ─── Chain / Blink animation timing ────────────────────────────────────────
/** ms the travelling light pulse takes to hop from one node to the next */
const CHAIN_TRAVEL_MS = 180
/** ms the blink highlight takes to rise from 0 → peak opacity */
const BLINK_RISE_MS = 90
/** ms the blink highlight takes to fade from peak → 0 */
const BLINK_FADE_MS = 600
/** ms pause between one chain run finishing and the next starting */
const CHAIN_GAP_MS = 90
/**
 * ms the verified-node fill face eases in after verification (ease-out curve).
 * Fill starts at UNVERIFIED_BASE_FILL_OPACITY and rises to VERIFIED_FILL_OPACITIES_BY_DEPTH.
 * Edge outlines and the v/x label are also animated within this window.
 */
const FILL_TRANSITION_MS = 300
/** radians/second the whole scene rotates around the Y axis (spin) */
const ROTATION_SPEED_Y = 0.0075
/** radians/second the whole scene tilts around the X axis (nod). 1/10 of Y speed */
const ROTATION_SPEED_X = 0.00075

// ─── Colors ─────────────────────────────────────────────────────────────────
/**
 * Fill/edge color for all nodes before and after positive verification.
 * All nodes start TEAL regardless of final state; negative nodes flip to RED
 * in useFrame at the moment their verification transition starts.
 */
const TEAL = 'hsl(174, 80%, 42%)'
/** Fill/edge color applied to negatively verified (buggy) nodes at transition start */
const RED = 'hsl(25, 95%, 53%)'
/** Color of the static connector lines drawn along each chain path */
const CHAIN_LINE_COLOR = 'hsl(220, 60%, 60%)'
/** Opacity of chain connector lines at depth 0 (root / near end) */
const CHAIN_LINE_OPACITY_MIN = 0.03
/** Opacity of chain connector lines at max depth (leaf / far end) */
const CHAIN_LINE_OPACITY_MAX = 0.95
/** Constant perpendicular offset (world units) for the decorative bezier arc alongside each connector */
const CHAIN_CURVE_OFFSET = 0.05
/** Duration of the "all verified" chain glow transition in ms */
const ALL_VERIFIED_TRANSITION_MS = 2500
/** Final chain line color at leaf end for a positive (no-bug) chain when all verified */
const VERIFIED_CHAIN_POSITIVE_COLOR = '#c0fff8' // near-white teal
/** Final chain line color at leaf end for a negative (bug) chain when all verified */
const VERIFIED_CHAIN_NEGATIVE_COLOR = '#ff3333' // saturated red

// ─── Post-processing (dark mode only, hardcoded) ─────────────────────────────
const GRAIN_ENABLED = true

/** Default values for the post-processing effect sliders (UI-tunable at runtime via the panel) */
const DEFAULT_OPTIONS = {
  lens: { x: 0.05, y: 0.05 },
  grain: { opacity: 0.5 },
  glow: { intensity: 1.1, luminanceThreshold: 0.01, radius: 1.0 },
} as const

// ─── Node cube visuals ───────────────────────────────────────────────────────
const DEBUG = false
/**
 * Fill face opacity for verified nodes, indexed by tree depth (0 = root).
 * Deeper (smaller) nodes get higher opacity to compensate for their smaller size.
 * Values are the transition TARGET; the fill eases in from UNVERIFIED_BASE_FILL_OPACITY.
 */
const VERIFIED_FILL_OPACITIES_BY_DEPTH = [0.05, 0.1, 0.15, 0.3, 0.4, 0.6, 0.7]
/**
 * Fill face opacity for ALL nodes in their unverified (idle) state.
 * Creates a faint ghost of the structure before any verification runs.
 * The fill transition eases FROM this value, not from 0.
 */
const UNVERIFIED_BASE_FILL_OPACITY = 0.03
/** Opacity of the dashed edge outlines on verified nodes — fades in alongside the fill */
const VERIFIED_EDGE_OPACITY = 1
/** Dash length for dashed edge lines (Three.js world units) */
const DASH_SIZE = 0.002
/** Gap length between dashes (Three.js world units) */
const GAP_SIZE = 0.005
/** Offset of the v/x label from the node surface along X (world units) */
const LABEL_OFFSET_X = 0.003
/** Font size of the v/x label text (world units) */
const INFO_TEXT_SIZE = 0.005
const LABEL_FONT =
  'https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@5.0.8/files/space-grotesk-latin-400-normal.woff'

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  CUBE STRUCTURE  (R3F component)                                         ║
// ║  Owns the full scene graph: skeleton, fills, edges, labels, blink.       ║
// ║  All geometry pre-built at mount; useFrame drives everything imperatively.║
// ╚══════════════════════════════════════════════════════════════════════════╝

type VerifyState = 'positive' | 'negative'

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  SCENE DATA                                                              ║
// ║  Pure data (cubes, chains, states) imported from scene-data.ts.         ║
// ║  Only Three.js geometry construction happens here — no generation,      ║
// ║  no randomness, no DFS traversal.                                       ║
// ╚══════════════════════════════════════════════════════════════════════════╝

const SCENE_DATA = (() => {
  const cubes = STATIC_CUBES
  const parentIndices = STATIC_PARENT_INDICES
  const chains = STATIC_CHAINS
  const depthMap = new Map<number, number>(STATIC_DEPTH_MAP)
  const maxDepth = STATIC_MAX_DEPTH
  const lineage = new Map<number, string>(STATIC_LINEAGE)
  const children = new Map<number, number[]>(STATIC_CHILDREN)
  const blinkCubeIndices = STATIC_BLINK_CUBE_INDICES
  const blinkCubes = blinkCubeIndices.map((idx) => cubes[idx])
  const cubeToChainPos = new Map(STATIC_CUBE_TO_CHAIN_POS)
  const precomputedNodeStates = new Map<
    number,
    { state: VerifyState; targetOpacity: number }
  >(
    STATIC_NODE_STATES as Array<
      [number, { state: VerifyState; targetOpacity: number }]
    >,
  )

  // ── Skeleton edge geometry ───────────────────────────────────────────────
  const allPositions: number[] = []
  for (const cube of cubes) allPositions.push(...cubeToLineSegments(cube))
  let minZ = Infinity,
    maxZ = -Infinity
  for (let i = 2; i < allPositions.length; i += 3) {
    minZ = Math.min(minZ, allPositions[i])
    maxZ = Math.max(maxZ, allPositions[i])
  }
  const range = maxZ - minZ || 1
  const colors: number[] = []
  for (let i = 2; i < allPositions.length; i += 3) {
    const t = (allPositions[i] - minZ) / range
    const [r, g, b] = depthToColor(t)
    colors.push(r, g, b)
  }
  const geom = new THREE.BufferGeometry()
  geom.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(allPositions, 3),
  )
  geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geom.computeBoundingSphere()

  // ── Chain connector segments + bezier curves ─────────────────────────────
  const segmentMap = new Map<
    string,
    { geometry: THREE.BufferGeometry; opacity: number }
  >()
  const chainCurveSegments: Array<{
    geometry: THREE.BufferGeometry
    opacity: number
  }> = []
  const _dir = new THREE.Vector3(),
    _up = new THREE.Vector3()
  const _perp = new THREE.Vector3(),
    _pA = new THREE.Vector3()
  const _pB = new THREE.Vector3(),
    _cp1 = new THREE.Vector3()

  for (const chain of chains) {
    for (let i = 0; i < chain.length - 1; i++) {
      const idx0 = chain[i],
        idx1 = chain[i + 1]
      const key = `${Math.min(idx0, idx1)}-${Math.max(idx0, idx1)}`
      if (segmentMap.has(key)) continue
      const c0 = cubes[idx0],
        c1 = cubes[idx1]
      const depthTo = depthMap.get(idx1) ?? 0
      if (c0 && c1 && maxDepth > 0) {
        const t = depthTo / maxDepth
        const opacity =
          CHAIN_LINE_OPACITY_MIN +
          (CHAIN_LINE_OPACITY_MAX - CHAIN_LINE_OPACITY_MIN) * t
        const g = new THREE.BufferGeometry()
        g.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(
            [c0.cx, c0.cy, c0.cz, c1.cx, c1.cy, c1.cz],
            3,
          ),
        )
        g.computeBoundingSphere()
        segmentMap.set(key, { geometry: g, opacity })

        _pA.set(c0.cx, c0.cy, c0.cz)
        _pB.set(c1.cx, c1.cy, c1.cz)
        _dir.subVectors(_pB, _pA).normalize()
        _up.set(
          Math.abs(_dir.y) < 0.85 ? 0 : 1,
          Math.abs(_dir.y) < 0.85 ? 1 : 0,
          0,
        )
        _perp.crossVectors(_dir, _up).normalize()
        _perp.multiplyScalar(
          CHAIN_CURVE_OFFSET *
            (2 - 1.5 * t) *
            ((idx0 + idx1) % 2 === 0 ? 1 : -1),
        )
        _cp1.addVectors(_pA, _pB).multiplyScalar(0.5).add(_perp)
        const curve = new THREE.QuadraticBezierCurve3(
          _pA.clone(),
          _cp1.clone(),
          _pB.clone(),
        )
        const pts = curve.getPoints(24)
        const pairPositions: number[] = [],
          lineDistances: number[] = []
        for (let p = 0; p < pts.length - 1; p++) {
          pairPositions.push(
            pts[p].x,
            pts[p].y,
            pts[p].z,
            pts[p + 1].x,
            pts[p + 1].y,
            pts[p + 1].z,
          )
          const dx = pts[p + 1].x - pts[p].x,
            dy = pts[p + 1].y - pts[p].y,
            dz = pts[p + 1].z - pts[p].z
          lineDistances.push(0, Math.sqrt(dx * dx + dy * dy + dz * dz))
        }
        const curveGeom = new THREE.BufferGeometry()
        curveGeom.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(pairPositions, 3),
        )
        curveGeom.setAttribute(
          'lineDistance',
          new THREE.Float32BufferAttribute(lineDistances, 1),
        )
        curveGeom.computeBoundingSphere()
        chainCurveSegments.push({
          geometry: curveGeom,
          opacity: opacity * 0.7,
        })
      }
    }
  }
  const chainLineSegments = Array.from(segmentMap.values())
  const chainSegmentKeyToIdx = new Map<string, number>()
  Array.from(segmentMap.keys()).forEach((key, i) =>
    chainSegmentKeyToIdx.set(key, i),
  )

  // ── BoxGeometry + EdgesGeometry per cube (size-cached) ───────────────────
  const geomSizeCache = new Map<
    number,
    { box: THREE.BoxGeometry; edges: THREE.EdgesGeometry }
  >()
  const allCubeGeoms = cubes.map((cube) => {
    const size = cube.half * 2
    if (!geomSizeCache.has(size)) {
      const box = new THREE.BoxGeometry(size, size, size)
      geomSizeCache.set(size, { box, edges: new THREE.EdgesGeometry(box) })
    }
    return geomSizeCache.get(size)!
  })

  // ── Per-segment metadata ─────────────────────────────────────────────────
  const segmentMeta: Array<{ depthRatio: number; isNegative: boolean }> =
    chainLineSegments.map(() => ({ depthRatio: 0, isNegative: false }))
  for (const chain of chains) {
    if (chain.length < 2) continue
    const leafIdx = chain[chain.length - 1]
    const isNeg = precomputedNodeStates.get(leafIdx)?.state === 'negative'
    for (let i = 0; i < chain.length - 1; i++) {
      const key = `${Math.min(chain[i], chain[i + 1])}-${Math.max(chain[i], chain[i + 1])}`
      const segIdx = chainSegmentKeyToIdx.get(key)
      if (segIdx === undefined) continue
      const ratio = (i + 1) / (chain.length - 1)
      if (ratio >= segmentMeta[segIdx].depthRatio) {
        segmentMeta[segIdx].depthRatio = ratio
        segmentMeta[segIdx].isNegative = isNeg
      }
    }
  }

  // ── Blink geometries ─────────────────────────────────────────────────────
  const blinkGeometries = blinkCubes.map((cube) => {
    const g = new THREE.BufferGeometry()
    g.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(cubeToLineSegments(cube), 3),
    )
    return g
  })

  return {
    geometry: geom,
    chainLineSegments,
    chainCurveSegments,
    chainSegmentKeyToIdx,
    segmentMeta,
    allCubeGeoms,
    precomputedNodeStates,
    blinkCubes,
    blinkGeometries,
    chains,
    cubeToChainPos,
    blinkCubeIndices,
    parentIndices,
    children,
    lineage,
    depthMap,
    maxDepth,
    cubes,
  }
})()

function CubeStructure({
  scrollProgressRef,
  zoomProgressRef,
  nodeScrollProgressRef,
}: {
  scrollProgressRef: React.RefObject<number>
  zoomProgressRef: React.RefObject<number>
  nodeScrollProgressRef: React.RefObject<number>
}) {
  const groupRef = useRef<THREE.Group>(null)
  /** Outer group: scroll-driven X tilt (arc equivalent). Inner groupRef: idle Y spin. */
  const scrollGroupRef = useRef<THREE.Group>(null)
  const scrollXRotRef = useRef(0) // smooth-lerped scroll tilt angle
  const smoothCameraZRef = useRef(-1) // -1 = uninitialised; set to camera.position.z on first frame
  const initialCameraZRef = useRef(-1)
  const blinkMaterialsRef = useRef<THREE.LineBasicMaterial[]>([])
  /** chain connector line material per segment index — driven by useFrame blink pass */
  const chainLineMaterialsRef = useRef<(THREE.LineBasicMaterial | null)[]>([])
  const chainStateRef = useRef({
    chainIdx: CHAIN_PLAY_ORDER[0] ?? 0,
    startTime: 0,
  })
  /** how many chains from CHAIN_PLAY_ORDER have been started so far */
  const playOrderIdxRef = useRef(0)
  /** counts how many chain leaves have been time-verified; latches once INITIAL_CHAINS_COUNT reached */
  const initialVerifiedCountRef = useRef(0)
  const timeBasedVerifyDoneRef = useRef(false)
  /** highest SCROLL_VERIFY_NODE_ORDER index processed so far — avoids re-iterating on every frame */
  const scrollVerifyWatermarkRef = useRef(0)
  // Scratch objects for frustum-visibility check — allocated once, reused every chain switch
  const frustumRef = useRef(new THREE.Frustum())
  const projMatrixRef = useRef(new THREE.Matrix4())
  const leafPosRef = useRef(new THREE.Vector3())
  /** Timestamp (ms) when all chains were first fully verified — triggers final glow state */
  const allVerifiedRef = useRef<number | null>(null)
  // Pre-built Color objects for chain line lerping — avoids string parsing in useFrame
  const chainLineBaseColor = useRef(new THREE.Color(CHAIN_LINE_COLOR))
  const chainPositiveEndColor = useRef(
    new THREE.Color(VERIFIED_CHAIN_POSITIVE_COLOR),
  )
  const chainNegativeEndColor = useRef(
    new THREE.Color(VERIFIED_CHAIN_NEGATIVE_COLOR),
  )
  const cubeVerificationRef = useRef<Map<number, VerifyState>>(new Map())
  const fillTransitionStartRef = useRef<Map<number, number>>(new Map())
  /** fill face material per cube — pre-registered at mount, driven by useFrame */
  const fillMaterialsRef = useRef<Map<number, THREE.MeshBasicMaterial>>(
    new Map(),
  )
  /** edge outline material per cube — pre-registered at mount, driven by useFrame */
  const edgeMaterialsRef = useRef<Map<number, THREE.LineDashedMaterial>>(
    new Map(),
  )
  /** label group per cube — pre-registered at mount, shown by useFrame when transition ends */
  const labelGroupsRef = useRef<Map<number, THREE.Group>>(new Map())

  const {
    geometry,
    chainLineSegments,
    chainCurveSegments,
    chainSegmentKeyToIdx,
    segmentMeta,
    allCubeGeoms,
    precomputedNodeStates,
    blinkCubes,
    blinkGeometries,
    chains,
    cubeToChainPos,
    blinkCubeIndices,
    parentIndices,
    children,
    lineage,
    depthMap,
    maxDepth,
    cubes,
  } = SCENE_DATA

  useFrame(({ clock, camera }) => {
    if (groupRef.current) {
      const elapsed = clock.getElapsedTime()
      // Inner group: continuous Y spin — independent of scroll
      groupRef.current.rotation.y = elapsed * ROTATION_SPEED_Y
    }
    if (scrollGroupRef.current) {
      // Outer group: scroll-driven X tilt — same arc as camera would travel.
      // θ 0→π maps front→top→opposite/inverted, matching the old camera arc exactly.
      const raw = scrollProgressRef.current ?? 0
      const eased =
        raw < 0.5 ? 4 * raw * raw * raw : 1 - Math.pow(-2 * raw + 2, 3) / 2
      const targetX = eased * -Math.PI
      scrollXRotRef.current += (targetX - scrollXRotRef.current) * 0.06
      scrollGroupRef.current.rotation.x = scrollXRotRef.current
    }

    // Camera zoom — lerps toward the sculpture centre as user scrolls through Select Features
    // into the Underlying Technologies section, using the same cubic ease + lerp dynamics as tilt.
    if (smoothCameraZRef.current < 0) {
      // First frame: capture the initial camera z set by the Canvas camera prop
      smoothCameraZRef.current = camera.position.z
      initialCameraZRef.current = camera.position.z
    }
    {
      const rawZoom = zoomProgressRef.current ?? 0
      const easedZoom =
        rawZoom < 0.5
          ? 4 * rawZoom * rawZoom * rawZoom
          : 1 - Math.pow(-2 * rawZoom + 2, 3) / 2
      const targetZ = initialCameraZRef.current * (1 - easedZoom * 0.5)
      smoothCameraZRef.current += (targetZ - smoothCameraZRef.current) * 0.06
      camera.position.z = smoothCameraZRef.current
    }

    const t = clock.getElapsedTime() * 1000
    const { chainIdx, startTime } = chainStateRef.current
    const chain = chains[chainIdx] ?? chains[0]
    if (!chain) return

    const verification = cubeVerificationRef.current
    const effectiveChain = chain.filter((idx) => !verification.has(idx))
    const chainDuration =
      effectiveChain.length * CHAIN_TRAVEL_MS +
      BLINK_RISE_MS +
      BLINK_FADE_MS +
      CHAIN_GAP_MS

    if (chains.length > 0 && t - startTime > chainDuration) {
      if (effectiveChain.length > 0 && !timeBasedVerifyDoneRef.current) {
        const lastCubeIdx = effectiveChain[effectiveChain.length - 1]
        // Use pre-computed state — no runtime random, no React setState
        const nodeState = precomputedNodeStates.get(lastCubeIdx)
        verification.set(lastCubeIdx, nodeState?.state ?? 'positive')

        // Bubble up: propagate using pre-computed parent states
        let p = parentIndices[lastCubeIdx]
        while (p >= 0) {
          const siblings = children.get(p) ?? []
          if (!siblings.every((c) => verification.has(c))) break
          const parentState = precomputedNodeStates.get(p)
          verification.set(p, parentState?.state ?? 'positive')
          p = parentIndices[p]
        }
        // No setVerificationSignal — geometry is pre-existing, useFrame drives everything

        initialVerifiedCountRef.current++
        if (initialVerifiedCountRef.current >= INITIAL_CHAINS_COUNT) {
          timeBasedVerifyDoneRef.current = true
        }
      }

      // Pick the next chain. Priority:
      //   1. unverified leaf + visible in frustum  (ideal: makes progress AND looks good)
      //   2. unverified leaf, any position         (must make progress even if off-screen)
      //   3. verified leaf + visible               (all done, keep the pulse going)
      //   4. any chain                             (final fallback)
      projMatrixRef.current.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse,
      )
      frustumRef.current.setFromProjectionMatrix(projMatrixRef.current)
      const groupMatrix = groupRef.current?.matrixWorld

      const unverifiedVisible: number[] = []
      const unverifiedAny: number[] = []
      const verifiedVisible: number[] = []
      for (let ci = 0; ci < chains.length; ci++) {
        const leafIdx = chains[ci][chains[ci].length - 1]
        const leafCube = cubes[leafIdx]
        if (!leafCube) continue
        leafPosRef.current.set(leafCube.cx, leafCube.cy, leafCube.cz)
        if (groupMatrix) leafPosRef.current.applyMatrix4(groupMatrix)
        const isVisible = frustumRef.current.containsPoint(leafPosRef.current)
        const isDone = verification.has(leafIdx)
        if (!isDone && isVisible) unverifiedVisible.push(ci)
        else if (!isDone) unverifiedAny.push(ci)
        else if (isDone && isVisible) verifiedVisible.push(ci)
      }

      // If CHAIN_PLAY_ORDER has more entries, consume the next one first
      const playIdx = playOrderIdxRef.current
      let nextChainIdx: number
      if (playIdx < CHAIN_PLAY_ORDER.length) {
        nextChainIdx = CHAIN_PLAY_ORDER[playIdx]!
        playOrderIdxRef.current = playIdx + 1
      } else {
        const pool =
          unverifiedVisible.length > 0
            ? unverifiedVisible
            : unverifiedAny.length > 0
              ? unverifiedAny
              : verifiedVisible.length > 0
                ? verifiedVisible
                : chains.map((_, ci) => ci)
        nextChainIdx =
          pool[Math.floor(random(t * 19) * pool.length) % pool.length]!
      }

      chainStateRef.current = {
        chainIdx: nextChainIdx,
        startTime: t,
      }
    }

    // ── Scroll-driven node verification ─────────────────────────────────────
    // Runs every frame from page load — scroll progress (0 = top, 1 = bottom)
    // determines how far into SCROLL_VERIFY_NODE_ORDER has been lit.
    // 80% of all nodes are verified by the time the user reaches the page bottom.
    // Nodes already verified by the time-based initial chains are silently skipped.
    // scrollVerifyWatermarkRef avoids re-iterating already-processed indices each frame.
    {
      const scrollP = nodeScrollProgressRef.current ?? 0
      const targetCount = Math.floor(
        scrollP * SCROLL_VERIFY_NODE_ORDER.length * 0.5,
      )
      const from = scrollVerifyWatermarkRef.current
      if (targetCount > from) {
        scrollVerifyWatermarkRef.current = targetCount
        for (let i = from; i < targetCount; i++) {
          const leafIdx = SCROLL_VERIFY_NODE_ORDER[i]
          if (leafIdx === undefined || verification.has(leafIdx)) continue
          const nodeState = precomputedNodeStates.get(leafIdx)
          verification.set(leafIdx, nodeState?.state ?? 'positive')
          let p = parentIndices[leafIdx]
          while (p >= 0) {
            const siblings = children.get(p) ?? []
            if (!siblings.every((c) => verification.has(c))) break
            const parentState = precomputedNodeStates.get(p)
            verification.set(p, parentState?.state ?? 'positive')
            p = parentIndices[p]
          }
        }
      }
    }

    const totalFadeMs = BLINK_RISE_MS + BLINK_FADE_MS

    for (let i = 0; i < blinkCubeIndices.length; i++) {
      const mat = blinkMaterialsRef.current[i]
      if (!mat) continue

      const cubeIdx = blinkCubeIndices[i]
      const verifyState = verification.get(cubeIdx)

      if (verifyState) {
        mat.visible = false
        continue
      }

      const chainPositions = cubeToChainPos.get(cubeIdx)
      const inCurrentChain = chainPositions?.find(
        (cp) => cp.chainIdx === chainIdx,
      )
      if (!inCurrentChain) {
        mat.visible = false
        mat.opacity = 0
        continue
      }

      const posInEffective = effectiveChain.indexOf(cubeIdx)
      if (posInEffective < 0) {
        mat.visible = false
        mat.opacity = 0
        continue
      }

      const fireTime = startTime + posInEffective * CHAIN_TRAVEL_MS
      const dt = t - fireTime

      if (dt < 0 || dt > totalFadeMs) {
        mat.visible = false
        mat.opacity = 0
        continue
      }

      mat.visible = true
      mat.color.set('#7dd3fc')
      if (dt <= BLINK_RISE_MS) {
        mat.opacity = (dt / BLINK_RISE_MS) * 0.9
      } else {
        mat.opacity = 0.9 * (1 - (dt - BLINK_RISE_MS) / BLINK_FADE_MS)
      }
    }

    // ── Chain connector line highlights ─────────────────────────────────────
    const chainLineMats = chainLineMaterialsRef.current

    // Detect when every chain leaf has been verified (fires once, latches forever)
    if (allVerifiedRef.current === null && chains.length > 0) {
      const verifiedLeaves = chains.filter((ch) =>
        verification.has(ch[ch.length - 1]),
      ).length
      if (verifiedLeaves === chains.length) {
        allVerifiedRef.current = t
      }
    }

    if (allVerifiedRef.current !== null) {
      // ── "All verified" glow: gradient from chain-line color → white-teal / bright-red ──
      // depthRatio drives how far along the gradient each segment sits:
      //   0 (root) → stays near base color,  1 (leaf) → fully at target color
      const elapsed = t - allVerifiedRef.current
      const progress = Math.min(1, elapsed / ALL_VERIFIED_TRANSITION_MS)
      const eased = 1 - (1 - progress) ** 3 // ease-out cubic

      for (let i = 0; i < chainLineSegments.length; i++) {
        const mat = chainLineMats[i]
        if (!mat) continue
        const { depthRatio, isNegative } = segmentMeta[i]
        const targetColor = isNegative
          ? chainNegativeEndColor.current
          : chainPositiveEndColor.current
        mat.color.lerpColors(
          chainLineBaseColor.current,
          targetColor,
          eased * depthRatio,
        )
        mat.opacity =
          chainLineSegments[i].opacity +
          (0.9 - chainLineSegments[i].opacity) * eased
      }
    } else {
      // ── Normal: reset all to base, then boost segment leading to blink pulse ──
      for (let i = 0; i < chainLineSegments.length; i++) {
        const mat = chainLineMats[i]
        if (mat) {
          mat.opacity = chainLineSegments[i].opacity
          mat.color.copy(chainLineBaseColor.current)
        }
      }
      for (let i = 0; i < blinkCubeIndices.length; i++) {
        const blinkMat = blinkMaterialsRef.current[i]
        if (!blinkMat?.visible) continue

        const cubeIdx = blinkCubeIndices[i]
        const posInEffective = effectiveChain.indexOf(cubeIdx)
        if (posInEffective <= 0) continue // root has no incoming segment

        const prevCubeIdx = effectiveChain[posInEffective - 1]
        const segKey = `${Math.min(cubeIdx, prevCubeIdx)}-${Math.max(cubeIdx, prevCubeIdx)}`
        const segIdx = chainSegmentKeyToIdx.get(segKey)
        if (segIdx === undefined) continue

        const segMat = chainLineMats[segIdx]
        if (!segMat) continue

        const pulseStrength = blinkMat.opacity / 0.9
        const base = chainLineSegments[segIdx].opacity
        segMat.opacity = base + (1 - base) * pulseStrength
        segMat.color.set('#7dd3fc') // match the blink cube glow
      }
    }

    // Ease-out opacity transition for verified nodes — drives fill, edges, and label visibility.
    // Root (cubeIdx === 0) is intentionally excluded: it lights up only in the all-verified glow.
    const fillMats = fillMaterialsRef.current
    const edgeMats = edgeMaterialsRef.current
    const labelGroups = labelGroupsRef.current
    const transitionStart = fillTransitionStartRef.current
    verification.forEach((_state, cubeIdx) => {
      if (cubeIdx === 0) return // root waits for all-verified glow
      const fillMat = fillMats.get(cubeIdx)
      if (!fillMat) return
      const nodeData = precomputedNodeStates.get(cubeIdx)
      const targetOpacity = nodeData?.targetOpacity ?? 0.05
      if (!transitionStart.has(cubeIdx)) {
        transitionStart.set(cubeIdx, t)
        // Flip to red at the moment of verification for negative nodes
        if (nodeData?.state === 'negative') {
          fillMat.color.set(RED)
          const edgeMat = edgeMats.get(cubeIdx)
          if (edgeMat) edgeMat.color.set(RED)
        }
      }
      const elapsed = t - transitionStart.get(cubeIdx)!
      if (elapsed >= FILL_TRANSITION_MS) {
        fillMat.opacity = targetOpacity
        const edgeMat = edgeMats.get(cubeIdx)
        if (edgeMat) edgeMat.opacity = VERIFIED_EDGE_OPACITY
        const labelGroup = labelGroups.get(cubeIdx)
        if (labelGroup) labelGroup.visible = true
        return
      }
      const easeOut = 1 - (1 - Math.min(1, elapsed / FILL_TRANSITION_MS)) ** 2
      fillMat.opacity =
        UNVERIFIED_BASE_FILL_OPACITY +
        easeOut * (targetOpacity - UNVERIFIED_BASE_FILL_OPACITY)
      const edgeMat = edgeMats.get(cubeIdx)
      if (edgeMat) edgeMat.opacity = easeOut * VERIFIED_EDGE_OPACITY
    })
  })

  return (
    <group ref={scrollGroupRef}>
      <group ref={groupRef}>
        {/* Skeleton edge lines for all cubes — vertex-colored by depth */}
        <lineSegments
          geometry={geometry}
          renderOrder={0}
          ref={(el) => el?.computeLineDistances?.()}
        >
          <lineDashedMaterial
            vertexColors
            transparent
            opacity={0.5}
            dashSize={DASH_SIZE}
            gapSize={GAP_SIZE}
          />
        </lineSegments>

        {/* Chain connector lines */}
        {chainLineSegments.map(({ geometry: segGeom, opacity }, idx) => (
          <lineSegments key={idx} geometry={segGeom}>
            <lineBasicMaterial
              ref={(mat) => {
                chainLineMaterialsRef.current[idx] = mat
              }}
              color={CHAIN_LINE_COLOR}
              transparent
              opacity={opacity}
              depthWrite={false}
            />
          </lineSegments>
        ))}

        {/* Decorative bezier arcs — dotted "string" alongside each connector (disabled) */}
        {/* {chainCurveSegments.map(({ geometry: curveGeom, opacity }, idx) => (
        <lineSegments key={idx} geometry={curveGeom}>
          <lineDashedMaterial
            color={CHAIN_LINE_COLOR}
            transparent
            opacity={opacity}
            depthWrite={false}
            dashSize={0.03}
            gapSize={0.03}
          />
        </lineSegments>
      ))} */}

        {/*
        All cube fills + edges + labels — pre-rendered at mount, always in the scene.
        Opacity and visibility are driven entirely by useFrame; no React re-render on verify.
        Fill starts at UNVERIFIED_BASE_FILL_OPACITY (ghost teal).
        On verification, useFrame eases fill opacity to targetOpacity, fades in edges, shows label.
      */}
        {cubes.map((cube, cubeIdx) => {
          const nodeData = precomputedNodeStates.get(cubeIdx)
          const nodeColor = TEAL // all nodes start green; color flips to red on verification in useFrame
          const labelOffset = cube.half + LABEL_OFFSET_X
          const topFrontOffset: [number, number, number] = [
            labelOffset,
            cube.half,
            cube.half,
          ]
          const childCount = children.get(cubeIdx)?.length ?? 0
          const vx = nodeData?.state === 'positive' ? 'v' : 'x'
          const labelText = DEBUG
            ? `${lineage.get(cubeIdx) ?? '?'}\n${childCount > 0 ? `${vx} ${childCount}` : vx}`
            : vx
          return (
            <group key={cubeIdx} position={[cube.cx, cube.cy, cube.cz]}>
              {/* Label — disabled for performance */}
              {/* <group
              ref={(g) => {
                if (g) labelGroupsRef.current.set(cubeIdx, g)
              }}
              visible={false}
            >
              <Billboard follow position={topFrontOffset}>
                <Text
                  font={LABEL_FONT}
                  fontSize={INFO_TEXT_SIZE}
                  color={nodeData?.state === 'positive' ? '#5eead4' : RED}
                  anchorX="center"
                  anchorY="middle"
                >
                  {labelText}
                </Text>
              </Billboard>
            </group> */}

              {/* Fill face — starts at base opacity, animated to targetOpacity on verify */}
              <mesh geometry={allCubeGeoms[cubeIdx]!.box} renderOrder={1}>
                <meshBasicMaterial
                  ref={(m) => {
                    if (m) {
                      fillMaterialsRef.current.set(cubeIdx, m)
                      m.opacity = UNVERIFIED_BASE_FILL_OPACITY
                    }
                  }}
                  color={nodeColor}
                  transparent
                  depthWrite={false}
                  alphaTest={0.005}
                  polygonOffset
                  polygonOffsetFactor={1}
                  polygonOffsetUnits={1}
                />
              </mesh>

              {/* Edge outline — opacity starts at 0, fades in on verify */}
              <lineSegments
                geometry={allCubeGeoms[cubeIdx]!.edges}
                renderOrder={2}
                ref={(el) => el?.computeLineDistances?.()}
              >
                <lineDashedMaterial
                  ref={(m) => {
                    if (m) {
                      edgeMaterialsRef.current.set(cubeIdx, m)
                      m.opacity = 0
                    }
                  }}
                  color={nodeColor}
                  transparent
                  opacity={0}
                  depthWrite={false}
                  dashSize={DASH_SIZE}
                  gapSize={GAP_SIZE}
                />
              </lineSegments>
            </group>
          )
        })}
        {blinkGeometries.map((geom, i) => (
          <lineSegments
            key={i}
            geometry={geom}
            ref={(el) => el?.computeLineDistances?.()}
          >
            <lineDashedMaterial
              ref={(m) => {
                if (m) blinkMaterialsRef.current[i] = m
              }}
              color="#7dd3fc"
              transparent
              opacity={0}
              depthWrite={false}
              dashSize={DASH_SIZE}
              gapSize={GAP_SIZE}
            />
          </lineSegments>
        ))}
      </group>
    </group>
  )
}

type EffectOptions = {
  lens: { x: number; y: number }
  grain: { opacity: number }
  glow: { intensity: number; luminanceThreshold: number; radius: number }
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  POST-PROCESSING  (R3F component)                                        ║
// ║  EffectComposer: Bloom → Vignette → BrightnessContrast →                 ║
// ║                  LensDistortion → Noise (grain)                          ║
// ║  Rebuilds EffectPass when options change (slider panel).                 ║
// ╚══════════════════════════════════════════════════════════════════════════╝

function PostProcessing({ options }: { options: EffectOptions }) {
  const { gl, scene, camera, size } = useThree()
  const composer = useRef<EffectComposer | null>(null)
  const effectPassRef = useRef<EffectPass | null>(null)

  const ua = navigator.userAgent
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
  const isFirefox = /firefox/i.test(ua)
  const useConservativeGrading = isSafari || isFirefox
  const brightness = useConservativeGrading ? -0.05 : -0.2
  const contrast = useConservativeGrading ? 0.0 : -0.1
  useEffect(() => {
    if (!composer.current) composer.current = new EffectComposer(gl)
    if (composer.current.passes.length === 0) {
      composer.current.addPass(new RenderPass(scene, camera))
    }
    if (effectPassRef.current) {
      const idx = composer.current.passes.indexOf(effectPassRef.current)
      if (idx > -1) {
        composer.current.removePass(effectPassRef.current)
        effectPassRef.current.dispose()
      }
    }
    const lensDistortion = new LensDistortionEffect({
      distortion: new THREE.Vector2(options.lens.x, options.lens.y),
      principalPoint: new THREE.Vector2(0, 0),
      focalLength: new THREE.Vector2(1, 1),
    })

    const effects: Effect[] = [
      new BloomEffect({
        intensity: options.glow.intensity,
        luminanceThreshold: options.glow.luminanceThreshold,
        radius: options.glow.radius,
      }),
      new VignetteEffect({ offset: 0.4, darkness: 0.7 }),
      new BrightnessContrastEffect({ brightness, contrast }),
      lensDistortion,
    ]
    if (GRAIN_ENABLED) {
      const grain = new NoiseEffect({
        blendFunction: BlendFunction.SCREEN,
        premultiply: true,
      })
      grain.blendMode.opacity.value = options.grain.opacity
      effects.push(grain)
    }
    const effectPass = new EffectPass(camera, ...effects)
    effectPassRef.current = effectPass
    composer.current.addPass(effectPass)
    return () => {
      composer.current?.dispose()
      composer.current = null
      effectPassRef.current = null
    }
  }, [gl, scene, camera, brightness, contrast, options])

  useEffect(() => {
    composer.current?.setSize(size.width, size.height)
  }, [size])

  useFrame(() => {
    composer.current?.render()
  }, 1)

  return null
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  SCENE ENVIRONMENT                                                       ║
// ╚══════════════════════════════════════════════════════════════════════════╝

function TransparentBackground() {
  const { scene } = useThree()
  useEffect(() => {
    scene.background = null
  }, [scene])
  return null
}

function SceneFog() {
  const { scene } = useThree()
  useEffect(() => {
    scene.fog = new THREE.FogExp2(0x0a0a0f, 0.4)
    return () => {
      scene.fog = null
    }
  }, [scene])
  return null
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  UI & LAYOUT                                                             ║
// ║  ParallaxWrapper, Slider component, ThreeHero root export.              ║
// ╚══════════════════════════════════════════════════════════════════════════╝

/** Set to true to re-enable the scroll-driven parallax (translateY * 0.5) on the canvas */
const PARALLAX_ENABLED = false

function ParallaxWrapper({ children }: { children: React.ReactNode }) {
  const [scrollY, setScrollY] = useState(0)
  useEffect(() => {
    if (!PARALLAX_ENABLED) return
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  if (!PARALLAX_ENABLED) return <>{children}</>
  return (
    <div
      style={{
        transform: `translateY(${scrollY * 0.5}px)`,
        willChange: 'transform',
      }}
    >
      {children}
    </div>
  )
}

export function ThreeHero() {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const options: EffectOptions = DEFAULT_OPTIONS

  // Tracks raw scroll progress (0–1) for CubeStructure scroll-tilt; ref = no re-renders
  const scrollProgressRef = useRef(0)
  // Tracks zoom progress (0–1): 0 = #complexity (Tackle Complexity) enters view, 1 = last section (page bottom)
  const zoomProgressRef = useRef(0)
  // Tracks full-page scroll progress (0–1): 0 = page top, 1 = page bottom — drives node verification
  const nodeScrollProgressRef = useRef(0)

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight })
    }
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const vh = window.innerHeight

      // Two viewport heights = full X-tilt arc (θ 0 → π).
      scrollProgressRef.current = Math.min(1, scrollY / (2 * vh))

      // Full-page scroll progress for scroll-driven node verification.
      const fullPageHeight = document.documentElement.scrollHeight - vh
      nodeScrollProgressRef.current =
        fullPageHeight > 0 ? Math.min(1, scrollY / fullPageHeight) : 0

      // Zoom progress: starts when #complexity (Tackle Complexity with Math and Logic) top enters view;
      // ends at the last section (bottom of page).
      const complexityEl = document.getElementById('complexity')
      const techEl = document.getElementById('technologies')
      if (complexityEl) {
        const complexityTop = complexityEl.getBoundingClientRect().top + scrollY
        const zoomStart = complexityTop - vh
        const zoomEnd = document.documentElement.scrollHeight - vh
        zoomProgressRef.current =
          zoomEnd > zoomStart
            ? Math.max(
                0,
                Math.min(1, (scrollY - zoomStart) / (zoomEnd - zoomStart)),
              )
            : 0
      } else {
        // Fallback: starts when #usecases-new bottom leaves the viewport; ends at page bottom.
        const ucEl = document.getElementById('usecases-new')
        if (ucEl) {
          const ucBottom = ucEl.getBoundingClientRect().bottom + scrollY
          const zoomStart = ucBottom - vh
          const zoomEnd = document.documentElement.scrollHeight - vh
          zoomProgressRef.current =
            zoomEnd > zoomStart
              ? Math.max(
                  0,
                  Math.min(1, (scrollY - zoomStart) / (zoomEnd - zoomStart)),
                )
              : 0
        } else {
          // Original: #select-features top → #technologies centre
          const sfEl = document.getElementById('select-features')
          if (sfEl && techEl) {
            const sfTop = sfEl.getBoundingClientRect().top + scrollY
            const techRect = techEl.getBoundingClientRect()
            const techAbsCenter = techRect.top + scrollY + techRect.height / 2
            const zoomStart = sfTop - vh
            const zoomEnd = techAbsCenter - vh / 2
            zoomProgressRef.current =
              zoomEnd > zoomStart
                ? Math.max(
                    0,
                    Math.min(1, (scrollY - zoomStart) / (zoomEnd - zoomStart)),
                  )
                : 0
          }
        }
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const cameraRadius = dimensions.width < 768 ? 1.8 : 1.5
  const cameraPosition: [number, number, number] = [0, 0, cameraRadius]
  const fov = dimensions.width < 768 ? 55 : 50

  const content = (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.0, delay: 0.3 }}
        className="fixed inset-0 h-screen w-screen"
      >
        <Canvas
          camera={{ position: cameraPosition, fov }}
          style={{ width: '100%', height: '100%' }}
          gl={{ antialias: true, alpha: true }}
        >
          <Suspense fallback={null}>
            <TransparentBackground />
            <SceneFog />
            <ambientLight intensity={0.5} />
            <directionalLight position={[2, 2, 2]} intensity={0.8} />
            <CubeStructure
              scrollProgressRef={scrollProgressRef}
              zoomProgressRef={zoomProgressRef}
              nodeScrollProgressRef={nodeScrollProgressRef}
            />
            <PostProcessing options={options} />
          </Suspense>
        </Canvas>
      </motion.div>
    </>
  )

  return <ParallaxWrapper>{content}</ParallaxWrapper>
}
